import { useMemo, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { BarChart, LineChart } from "./charts/Charts"
import { useLocale } from "./LocaleProvider"
import { useWorkerActivityHistoryForUser } from "../lib/queries"
import { formatDuration, formatRelative, formatTime, shortDate, windowSeconds } from "../lib/format"
import type { WorkerAccountRecord, WorkerActivityRecord, WorkerUserDay } from "../lib/api"

const RANGES = [7, 30, 90, 180]

interface WorkerOverviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  record: WorkerActivityRecord | null
}

function WorkerOverviewModal({ open, onOpenChange, record }: WorkerOverviewModalProps) {
  const { t } = useLocale()
  const [range, setRange] = useState(30)
  const { data, isLoading } = useWorkerActivityHistoryForUser(
    open ? (record?.userId ?? null) : null,
    range,
    open,
  )

  const daily: WorkerUserDay[] = useMemo(() => data?.daily ?? [], [data])
  const accounts: WorkerAccountRecord[] = useMemo(() => data?.accounts ?? [], [data])

  const summary = useMemo(() => {
    let totalHeartbeats = 0
    let activeDays = 0
    let totalWindow = 0
    let best: { date: string; seconds: number } | null = null
    for (const d of daily) {
      totalHeartbeats += d.heartbeatCount
      if (d.heartbeatCount > 0) activeDays += 1
      const secs = windowSeconds(d.firstSeenAt, d.lastSeenAt)
      totalWindow += secs
      if (!best || secs > best.seconds) best = { date: d.date, seconds: secs }
    }
    return {
      totalHeartbeats,
      activeDays,
      avg: activeDays ? totalWindow / activeDays : 0,
      best,
    }
  }, [daily])

  const windowData = daily.map((d) => ({
    label: shortDate(d.date),
    value: windowSeconds(d.firstSeenAt, d.lastSeenAt),
  }))
  const heartbeatsData = daily.map((d) => ({
    label: shortDate(d.date),
    value: d.heartbeatCount,
  }))

  const hourTotals = useMemo(() => {
    const totals = new Array<number>(24).fill(0)
    for (const d of daily) {
      for (let i = 0; i < 24; i++) totals[i] += d.hours?.[i] ?? 0
    }
    return totals
  }, [daily])

  const hourData = hourTotals.map((value, i) => ({
    label: String(i).padStart(2, "0"),
    value,
  }))

  const peakHour = useMemo(() => {
    let best = 0
    for (let i = 1; i < 24; i++) if (hourTotals[i] > hourTotals[best]) best = i
    return hourTotals[best] > 0 ? best : null
  }, [hourTotals])

  const accountTotal = useMemo(
    () => accounts.reduce((sum, a) => sum + a.heartbeatCount, 0),
    [accounts],
  )
  const accountData = accounts.map((a) => ({
    label: a.clientName || a.accountId || "—",
    value: a.heartbeatCount,
  }))

  const online = record?.online ?? false

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{record?.fullName ?? "—"}</span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                online
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  online ? "bg-emerald-500" : "bg-muted-foreground/60"
                }`}
              />
              {t(online ? "activity_online" : "activity_offline")}
            </span>
          </DialogTitle>
          {record?.email && <p className="text-sm text-muted-foreground">{record.email}</p>}
        </DialogHeader>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("activity_range")}</span>
          <div className="flex items-center gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`h-7 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                  range === r
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label={t("activity_active_days")} value={String(summary.activeDays)} />
          <Tile label={t("activity_avg_per_day")} value={formatDuration(summary.avg)} />
          <Tile
            label={t("activity_best_day")}
            value={summary.best ? formatDuration(summary.best.seconds) : "—"}
            sub={summary.best ? shortDate(summary.best.date) : undefined}
          />
          <Tile label={t("activity_total_heartbeats")} value={String(summary.totalHeartbeats)} />
          <Tile
            label={t("activity_peak_hour")}
            value={peakHour == null ? "—" : `${String(peakHour).padStart(2, "0")}:00`}
            sub={peakHour == null ? undefined : String(hourTotals[peakHour])}
          />
          <Tile
            label={t("activity_today_window")}
            value={
              record?.todayFirstSeenAt && record?.todayLastSeenAt
                ? `${formatTime(record.todayFirstSeenAt)}–${formatTime(record.todayLastSeenAt)}`
                : "—"
            }
          />
          <Tile
            label={t("activity_current_session")}
            value={formatDuration(record?.sessionSeconds ?? 0)}
          />
          <Tile label={t("activity_tabs")} value={String(record?.tabCount ?? 0)} />
          <Tile
            label={t("activity_last_seen")}
            value={formatRelative(record?.lastSeenAt ?? null, t("activity_never"))}
          />
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>
        ) : daily.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t("activity_empty")}
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <div className="mb-2 text-sm font-medium">{t("activity_chart_window")}</div>
              <BarChart data={windowData} formatValue={formatDuration} />
            </div>
            <div>
              <div className="mb-2 text-sm font-medium">{t("activity_chart_heartbeats")}</div>
              <LineChart data={heartbeatsData} formatValue={(v) => String(Math.round(v))} />
            </div>
            <div>
              <div className="mb-2 text-sm font-medium">{t("activity_chart_hourly")}</div>
              <BarChart data={hourData} formatValue={(v) => String(Math.round(v))} />
            </div>
            <div className="space-y-3">
              <div className="text-sm font-medium">{t("activity_accounts_title")}</div>
              {accounts.length === 0 ? (
                <div className="rounded-lg border px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("activity_accounts_empty")}
                </div>
              ) : (
                <>
                  <BarChart data={accountData} formatValue={(v) => String(Math.round(v))} />
                  <div className="rounded-lg border">
                    <div className="grid grid-cols-[1.1fr_1.4fr_1fr_auto_auto] gap-2 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
                      <span>{t("th_account")}</span>
                      <span>{t("th_client")}</span>
                      <span>{t("th_strategy")}</span>
                      <span className="text-right">{t("activity_requests")}</span>
                      <span className="text-right">{t("activity_share")}</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {accounts.map((a) => (
                        <div
                          key={a.accountId || "none"}
                          className="grid grid-cols-[1.1fr_1.4fr_1fr_auto_auto] items-center gap-2 border-b px-4 py-2 text-sm last:border-b-0"
                        >
                          <span className="font-mono truncate">
                            {a.accountId || t("activity_account_none")}
                          </span>
                          <span className="truncate text-muted-foreground">
                            {a.clientName || "—"}
                          </span>
                          <span className="truncate text-muted-foreground">
                            {a.strategy ? a.strategy.replace(/_/g, " ") : "—"}
                          </span>
                          <span className="text-right font-medium">{a.heartbeatCount}</span>
                          <span className="w-12 text-right text-muted-foreground">
                            {accountTotal > 0
                              ? `${Math.round((a.heartbeatCount / accountTotal) * 100)}%`
                              : "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="rounded-lg border">
              <div className="border-b px-4 py-2.5 text-sm font-medium">
                {t("activity_daily_span")}
              </div>
              <div className="max-h-56 overflow-y-auto">
                {daily.map((d) => (
                  <div
                    key={d.date}
                    className="flex items-center justify-between border-b px-4 py-2 text-sm last:border-b-0"
                  >
                    <span className="font-mono text-muted-foreground">{d.date}</span>
                    <span className="text-muted-foreground">
                      {formatTime(d.firstSeenAt)} – {formatTime(d.lastSeenAt)}
                    </span>
                    <span className="font-medium">
                      {formatDuration(windowSeconds(d.firstSeenAt, d.lastSeenAt))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  )
}

export default WorkerOverviewModal
